import { useEffect, useState } from "react";
import {
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { passtrackColors } from "../theme";
import { PHONE_COUNTRY_CODES } from "../types";
import {
  getInviteEmailError,
  getInvitePhoneError,
  isValidInviteEmail,
  isValidPhoneDigits,
} from "../utils/userValidation";

interface DuplicateEmailDialogProps {
  open: boolean;
  email: string;
  onClose: () => void;
}

export function DuplicateEmailDialog({
  open,
  email,
  onClose,
}: DuplicateEmailDialogProps) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Correo ya registrado</DialogTitle>
      <DialogContent>
        <Typography>
          Ya existe un usuario con el correo electrónico{" "}
          <strong>{email}</strong>. Verifica el correo e intenta de nuevo.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" autoFocus>
          Entendido
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface CreatePropertyErrorDialogProps {
  open: boolean;
  title: string;
  message: string;
  onClose: () => void;
}

export function CreatePropertyErrorDialog({
  open,
  title,
  message,
  onClose,
}: CreatePropertyErrorDialogProps) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography>{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" autoFocus>
          Entendido
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface IncompleteAccessGroupsDialogProps {
  open: boolean;
  incompleteCount: number;
  onCancel: () => void;
  onContinue: () => void;
}

export function IncompleteAccessGroupsDialog({
  open,
  incompleteCount,
  onCancel,
  onContinue,
}: IncompleteAccessGroupsDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Grupos de acceso incompletos</DialogTitle>
      <DialogContent>
        <Typography>
          Hay{" "}
          <strong>
            {incompleteCount}{" "}
            {incompleteCount === 1 ? "grupo de acceso" : "grupos de acceso"}
          </strong>{" "}
          con información incompleta. Para que un grupo se dé de alta debe tener
          nombre, puntos de acceso y roles.
        </Typography>
        <Typography sx={{ mt: 1.5 }}>
          Si continúas, esos grupos no se guardarán. Puedes cancelar y
          completarlos antes de crear la propiedad.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCancel} color="inherit">
          Cancelar
        </Button>
        <Button onClick={onContinue} variant="contained" autoFocus>
          Continuar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface DeleteAccessPointDialogProps {
  open: boolean;
  pointName: string;
  groupsToUpdate: string[];
  groupsToDelete: string[];
  onCancel: () => void;
  onConfirm: () => void;
}

export function DeleteAccessPointDialog({
  open,
  pointName,
  groupsToUpdate,
  groupsToDelete,
  onCancel,
  onConfirm,
}: DeleteAccessPointDialogProps) {
  const hasGroupImpact = groupsToUpdate.length > 0 || groupsToDelete.length > 0;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Eliminar punto de acceso</DialogTitle>
      <DialogContent>
        <Typography>
          ¿Quieres eliminar <strong>{pointName || "sin nombre"}</strong>?
        </Typography>

        {hasGroupImpact ? (
          <Box
            sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}
          >
            {groupsToUpdate.length > 0 && (
              <Typography>
                Se quitará de{" "}
                {groupsToUpdate.length === 1 ? "este grupo" : "estos grupos"},
                pero el grupo se mantendrá:
                <Box
                  component="ul"
                  sx={{
                    mt: 0.75,
                    mb: 0,
                    pl: 2.5,
                    color: passtrackColors.graphite,
                  }}
                >
                  {groupsToUpdate.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </Box>
              </Typography>
            )}
            {groupsToDelete.length > 0 && (
              <Typography>
                {groupsToDelete.length === 1
                  ? "Este punto de acceso es el único asociado al siguiente grupo. Si continúas, el grupo también se eliminará."
                  : "Este punto de acceso es el único asociado a los siguientes grupos. Si continúas, estos también se eliminarán."}
                <Box
                  component="ul"
                  sx={{
                    mt: 0.75,
                    mb: 0,
                    pl: 2.5,
                    color: passtrackColors.graphite,
                  }}
                >
                  {groupsToDelete.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </Box>
              </Typography>
            )}
          </Box>
        ) : (
          <Typography sx={{ mt: 1.5, color: passtrackColors.textMuted }}>
            Ningún grupo de acceso usa este punto.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCancel} color="inherit">
          Cancelar
        </Button>
        <Button onClick={onConfirm} variant="contained" color="error" autoFocus>
          Eliminar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export type DeleteUnitUserDecision = {
  userId: string;
  action: "delete" | "reassign";
  unitName?: string;
};

interface DeleteUnitDialogProps {
  open: boolean;
  unitName: string;
  affectedUsers: { id: string; name: string; email: string }[];
  availableUnits: { id: string; name: string }[];
  onCancel: () => void;
  onConfirm: (decisions: DeleteUnitUserDecision[]) => void;
}

type UserDecisionState = {
  action: "delete" | "reassign";
  unitName: string;
};

export function DeleteUnitDialog({
  open,
  unitName,
  affectedUsers,
  availableUnits,
  onCancel,
  onConfirm,
}: DeleteUnitDialogProps) {
  const canReassign = availableUnits.length > 0;
  const defaultUnitName = availableUnits[0]?.name ?? "";
  const [decisionsByUserId, setDecisionsByUserId] = useState<
    Record<string, UserDecisionState>
  >({});

  useEffect(() => {
    if (!open) return;
    const next: Record<string, UserDecisionState> = {};
    for (const user of affectedUsers) {
      next[user.id] = {
        action: canReassign ? "reassign" : "delete",
        unitName: defaultUnitName,
      };
    }
    setDecisionsByUserId(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateDecision = (
    userId: string,
    patch: Partial<UserDecisionState>,
  ) => {
    setDecisionsByUserId((prev) => ({
      ...prev,
      [userId]: {
        action: prev[userId]?.action ?? "delete",
        unitName: prev[userId]?.unitName ?? defaultUnitName,
        ...patch,
      },
    }));
  };

  const canConfirm =
    affectedUsers.length > 0 &&
    affectedUsers.every((user) => {
      const decision = decisionsByUserId[user.id];
      if (!decision) return false;
      if (decision.action === "delete") return true;
      return canReassign && Boolean(decision.unitName);
    });

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(
      affectedUsers.map((user) => {
        const decision = decisionsByUserId[user.id];
        if (decision?.action === "reassign" && decision.unitName) {
          return {
            userId: user.id,
            action: "reassign" as const,
            unitName: decision.unitName,
          };
        }
        return { userId: user.id, action: "delete" as const };
      }),
    );
  };

  const userWord =
    affectedUsers.length === 1 ? "usuario principal" : "usuarios principales";

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Eliminar unidad</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          La unidad <strong>{unitName || "sin nombre"}</strong> tiene{" "}
          <strong>
            {affectedUsers.length} {userWord}
          </strong>{" "}
          relacionados. Elige qué hacer con cada uno:
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {affectedUsers.map((user) => {
            const decision = decisionsByUserId[user.id] ?? {
              action: canReassign ? "reassign" : "delete",
              unitName: defaultUnitName,
            };
            return (
              <Box
                key={user.id}
                sx={{
                  border: `1px solid ${passtrackColors.borderSubtle}`,
                  borderRadius: 1.5,
                  p: 1.75,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: passtrackColors.ink,
                    mb: 0.25,
                  }}
                >
                  {user.name || "Sin nombre"}
                </Typography>
                {user.email ? (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: passtrackColors.textMuted,
                      mb: 1,
                    }}
                  >
                    {user.email}
                  </Typography>
                ) : null}

                <RadioGroup
                  value={decision.action}
                  onChange={(_, value) =>
                    updateDecision(user.id, {
                      action: value as "delete" | "reassign",
                    })
                  }
                >
                  <FormControlLabel
                    value="delete"
                    control={<Radio size="small" />}
                    label="Eliminar usuario"
                  />
                  <FormControlLabel
                    value="reassign"
                    control={<Radio size="small" />}
                    disabled={!canReassign}
                    label="Cambiar a otra unidad"
                  />
                </RadioGroup>

                {decision.action === "reassign" && canReassign && (
                  <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                    <InputLabel id={`delete-unit-target-${user.id}`}>
                      Nueva unidad
                    </InputLabel>
                    <Select
                      labelId={`delete-unit-target-${user.id}`}
                      label="Nueva unidad"
                      value={decision.unitName}
                      onChange={(e) =>
                        updateDecision(user.id, {
                          unitName: String(e.target.value),
                        })
                      }
                    >
                      {availableUnits.map((unit) => (
                        <MenuItem key={unit.id} value={unit.name}>
                          {unit.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            );
          })}
        </Box>

        {!canReassign && (
          <Typography
            sx={{ mt: 1.5, fontSize: 13, color: passtrackColors.textMuted }}
          >
            No hay otra unidad disponible para reasignar. Solo puedes eliminar a
            estos usuarios junto con la unidad.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCancel} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={!canConfirm}
          autoFocus
        >
          Eliminar unidad
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface UnitLinkedUsersDialogProps {
  open: boolean;
  unitName: string;
  users: { id: string; name: string; email: string; role: string }[];
  onClose: () => void;
}

export function UnitLinkedUsersDialog({
  open,
  unitName,
  users,
  onClose,
}: UnitLinkedUsersDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Usuarios de {unitName || "la unidad"}</DialogTitle>
      <DialogContent>
        {users.length === 0 ? (
          <Typography color={passtrackColors.textMuted}>
            No hay usuarios ligados a esta unidad.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              mt: 0.5,
            }}
          >
            {users.map((user) => (
              <Box
                key={user.id}
                sx={{
                  border: `1px solid ${passtrackColors.borderSubtle}`,
                  borderRadius: 1.5,
                  px: 1.75,
                  py: 1.25,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: passtrackColors.ink,
                  }}
                >
                  {user.name || "Sin nombre"}
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: passtrackColors.textMuted }}
                >
                  {user.role}
                  {user.email ? ` · ${user.email}` : ""}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} variant="contained" autoFocus>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export interface AddUserToUnitFormValues {
  name: string;
  email: string;
  phoneCountryCode: string;
  phoneNumber: string;
}

interface AddUserToUnitDialogProps {
  open: boolean;
  unitName: string;
  roleLabel: string;
  onCancel: () => void;
  onSave: (values: AddUserToUnitFormValues) => boolean;
}

const EMPTY_ADD_USER_FORM: AddUserToUnitFormValues = {
  name: "",
  email: "",
  phoneCountryCode: "+52",
  phoneNumber: "",
};

export function AddUserToUnitDialog({
  open,
  unitName,
  roleLabel,
  onCancel,
  onSave,
}: AddUserToUnitDialogProps) {
  const [form, setForm] =
    useState<AddUserToUnitFormValues>(EMPTY_ADD_USER_FORM);
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_ADD_USER_FORM);
    setAttempted(false);
  }, [open]);

  const emailError = getInviteEmailError(form.email);
  const phoneError = getInvitePhoneError(form.phoneNumber);
  const nameError = form.name.trim() ? undefined : "Ingresa un nombre.";
  const canSave =
    Boolean(form.name.trim()) &&
    isValidInviteEmail(form.email) &&
    isValidPhoneDigits(form.phoneNumber);

  const handleSave = () => {
    setAttempted(true);
    if (!canSave) return;
    const saved = onSave({
      ...form,
      name: form.name.trim(),
      email: form.email.trim(),
    });
    if (saved) {
      onCancel();
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Agregar usuario</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2, color: passtrackColors.graphite }}>
          Se agregará como <strong>{roleLabel}</strong> en la unidad{" "}
          <strong>{unitName || "sin nombre"}</strong>.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Nombre"
            size="small"
            fullWidth
            required
            value={form.name}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, name: e.target.value }))
            }
            placeholder="Ej. Julio Ramos"
            error={attempted && Boolean(nameError)}
            helperText={attempted ? nameError : undefined}
          />
          <TextField
            label="Correo electrónico"
            type="email"
            size="small"
            fullWidth
            required
            value={form.email}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, email: e.target.value }))
            }
            placeholder="julio@correo.com"
            error={attempted && Boolean(emailError)}
            helperText={attempted ? emailError : undefined}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 96 }} required>
              <InputLabel id="unit-add-user-phone-code-label">
                Código
              </InputLabel>
              <Select
                labelId="unit-add-user-phone-code-label"
                label="Código"
                value={form.phoneCountryCode}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    phoneCountryCode: String(e.target.value),
                  }))
                }
              >
                {PHONE_COUNTRY_CODES.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Teléfono"
              size="small"
              fullWidth
              required
              placeholder="10 dígitos"
              value={form.phoneNumber}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  phoneNumber: e.target.value.replace(/\D/g, "").slice(0, 10),
                }))
              }
              inputProps={{ maxLength: 10, inputMode: "numeric" }}
              error={attempted && Boolean(phoneError)}
              helperText={attempted ? phoneError : "10 dígitos sin espacios."}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCancel} color="inherit">
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" autoFocus>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export interface CreatePropertySuccessSummary {
  propertyName: string;
  pointsCount: number;
  doorsCount: number;
  groupsCount: number;
  unitsCount: number;
  usersCount: number;
}

interface CreatePropertySuccessDialogProps {
  open: boolean;
  summary: CreatePropertySuccessSummary | null;
  onClose: () => void;
}

export function CreatePropertySuccessDialog({
  open,
  summary,
  onClose,
}: CreatePropertySuccessDialogProps) {
  if (!summary) {
    return null;
  }

  const stats = [
    { value: summary.pointsCount, label: "Puntos de acceso" },
    { value: summary.doorsCount, label: "Puertas" },
    { value: summary.groupsCount, label: "Grupos de acceso" },
    { value: summary.unitsCount, label: "Unidades" },
    { value: summary.usersCount, label: "Usuarios" },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {summary.propertyName || "Tu propiedad"} está lista
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2.5, color: passtrackColors.graphite }}>
          La propiedad ya puede registrar entradas y salidas. Invita al resto de
          los usuarios desde el panel.
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(5, 1fr)" },
            gap: 1.25,
          }}
        >
          {stats.map((stat) => (
            <Box
              key={stat.label}
              sx={{
                background: passtrackColors.mist,
                borderRadius: 1.5,
                p: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 22,
                  color: passtrackColors.blue900,
                }}
              >
                {stat.value}
              </Typography>
              <Typography
                sx={{ fontSize: 11, color: passtrackColors.textMuted }}
              >
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} variant="contained" autoFocus>
          Ir al menú
        </Button>
      </DialogActions>
    </Dialog>
  );
}
